import React, { Dispatch, SetStateAction } from 'react';
import { StyleSheet, View, Text } from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import CustomTextField from '../../../../../../../components/CustomTextField';
import PressableStyled from '../../../../../../../components/PressableStyled';
import RegionDropdown from '../../../../../../../components/AssetManagement/AddAssetModal/components/RegionDropdown';
import { Area, Floor, Space } from '../../../../../../../components/Map/SetFloorsModal/FloorItem';
import Colors from '../../../../../../../constants/Colors';
import { AssetInstance } from '../../../../../../../constants/Types';
import DeleteIcon24 from '../../../../../../../components/24pxIcons/DeleteIcon24';
import Style from '../../../../../../../constants/Style';
import { Building } from '../../../../../../LocationManagement/components/AddBuildingModal';

interface LocationInfoProps {
  asset: AssetInstance,
  selectedBuilding: Building,
  selectedFloor: Floor,
  setSelectedFloor: Dispatch<SetStateAction<Floor>>,
  selectedArea: Area,
  setSelectedArea: Dispatch<SetStateAction<Area>>,
  selectedSpace: Space,
  setSelectedSpace: Dispatch<SetStateAction<Space>>,
  floorDropdownShown: boolean,
  setFloorDropdownShown: Dispatch<SetStateAction<boolean>>,
  areaDropdownShown: boolean,
  setAreaDropdownShown: Dispatch<SetStateAction<boolean>>,
  spaceDropdownShown: boolean,
  setSpaceDropdownShown: Dispatch<SetStateAction<boolean>>,
  closeDropdowns: () => void,
  floorInvalid: boolean,
  areaInvalid: boolean,
  spaceInvalid: boolean,
  areasCanHaveAssets: boolean,
  spacesCanHaveAssets: boolean,
}

const LocationInfo = ({
  asset, selectedFloor, setSelectedFloor, selectedArea, setSelectedArea, selectedSpace,
  setSelectedSpace, floorDropdownShown, setFloorDropdownShown, areaDropdownShown,
  setAreaDropdownShown, spaceDropdownShown, setSpaceDropdownShown, floorInvalid,
  areaInvalid, spaceInvalid, selectedBuilding, areasCanHaveAssets, spacesCanHaveAssets,
}: LocationInfoProps) => <>
    <CustomTextField
      required
      editable={false}
      title='Template'
      value={asset.templateName}
      testID='edit-asset-template-field'
    />
    <View testID='edit-asset-building-field'>
      <Text style={styles.dropdownFieldLabel}>{'Building *'}</Text>
      <PressableStyled
        style={styles.dropdownButton}
        disabled
      >
        <Text style={styles.dropdownBoxText}>{selectedBuilding?.Name}</Text>
      </PressableStyled>
      {selectedBuilding && <>
        <Text style={styles.dropdownFieldLabel}>{'1. Floor *'}</Text>
        <PressableStyled
          style={[styles.dropdownButton, floorInvalid && styles.dropdownButtonInvalid]}
          onPress={() => {
            setFloorDropdownShown(!floorDropdownShown);
            setAreaDropdownShown(false);
            setSpaceDropdownShown(false);
          }}
          testID='select-floor-button'
        >
          <Text style={styles.dropdownBoxText}>{selectedFloor?.Name ?? 'Select a floor'}</Text>
          <View style={{ alignItems: 'flex-end' }}>
            <FontAwesome
              size={20}
              style={{
                color: floorInvalid ? Colors.light.hereworksRed500
                  : Colors.light.hereworksLightBlue,
                marginRight: 12,
              }}
              name={floorDropdownShown ? 'angle-up' : 'angle-down'}
            />
          </View>
        </PressableStyled>
        {floorInvalid && <Text style={styles.errorText}>{'Please select a floor'}</Text>}
        {floorDropdownShown && <RegionDropdown
          regions={Object.values(selectedBuilding?.floors ?? {})
            ?.sort((prev: Floor, next: Floor) => prev.Index - next.Index)}
          selectedRegion={selectedFloor}
          setSelectedRegion={(floor: Floor) => {
            setSelectedFloor(floor);
            setSelectedArea(null);
            setSelectedSpace(null);
          }}
          testID='edit-asset-floor-list'
        />}
      </>}
      {(areasCanHaveAssets || spacesCanHaveAssets)
        && Object.values(selectedFloor?.areas ?? {}).length > 0
        && <View style={{ marginStart: 16 }}>
          <View style={{ flexDirection: 'row' }}>
            <View style={styles.connectorContainer}>
              <View style={styles.floorAreaConnector} />
              <View style={styles.connectorSpot} />
            </View>
            <View style={{ flex: 1 }}>
              <View style={Style.row}>
                <Text style={styles.dropdownFieldLabel}>{'2. Area'}</Text>
                {selectedArea && <PressableStyled
                  onPress={() => {
                    setSelectedArea(null);
                    setSelectedSpace(null);
                  }}
                  style={{ marginTop: styles.dropdownFieldLabel.marginTop }}
                >
                  <DeleteIcon24 />
                </PressableStyled>}
              </View>
              <PressableStyled
                style={[styles.dropdownButton, areaInvalid && styles.dropdownButtonInvalid]}
                onPress={() => {
                  setAreaDropdownShown(!areaDropdownShown);
                  setFloorDropdownShown(false);
                  setSpaceDropdownShown(false);
                }}
                testID='select-area-button'
              >
                <Text style={styles.dropdownBoxText}>
                  {selectedArea?.Name ?? 'Select an area'}
                </Text>
                <View style={{ alignItems: 'flex-end' }}>
                  <FontAwesome
                    size={20}
                    style={{
                      color: areaInvalid ? Colors.light.hereworksRed500
                        : Colors.light.hereworksLightBlue,
                      marginRight: 12,
                    }}
                    name={areaDropdownShown ? 'angle-up' : 'angle-down'}
                  />
                </View>
              </PressableStyled>
            </View>
          </View>
          <View style={{ marginStart: 20 }}>
            {areaInvalid && <Text style={styles.errorText}>
              {'Please select an area'}
            </Text>}
            {areaDropdownShown && <RegionDropdown
              regions={Object.values(selectedFloor?.areas ?? {})
                ?.sort((prev: Area, next: Area) => prev.Name.localeCompare(next.Name))}
              selectedRegion={selectedArea}
              setSelectedRegion={(area: Area) => {
                setSelectedArea(area);
                setSelectedSpace(null);
              }}
              testID='edit-asset-area-list'
            />}
          </View>
        </View>}
      {spacesCanHaveAssets && Object.values(selectedArea?.spaces ?? {}).length > 0
        && <View style={{ marginStart: 44 }}>
          <View style={{ flexDirection: 'row' }}>
            <View style={styles.connectorContainer}>
              <View style={styles.floorAreaConnector} />
              <View style={styles.connectorSpot} />
            </View>
            <View style={{ flex: 1 }}>
              <View style={Style.row}>
                <Text style={styles.dropdownFieldLabel}>{'3. Space'}</Text>
                {selectedSpace && <PressableStyled
                  onPress={() => setSelectedSpace(null)}
                  style={{ marginTop: styles.dropdownFieldLabel.marginTop }}
                >
                  <DeleteIcon24 />
                </PressableStyled>}
              </View>
              <PressableStyled
                style={[styles.dropdownButton, areaInvalid && styles.dropdownButtonInvalid]}
                onPress={() => {
                  setSpaceDropdownShown(!spaceDropdownShown);
                  setFloorDropdownShown(false);
                  setAreaDropdownShown(false);
                }}
                testID='select-space-button'
              >
                <Text style={styles.dropdownBoxText}>
                  {selectedSpace?.Name ?? 'Select a space'}
                </Text>
                <View style={{ alignItems: 'flex-end' }}>
                  <FontAwesome
                    size={20}
                    style={{
                      color: spaceInvalid ? Colors.light.hereworksRed500
                        : Colors.light.hereworksLightBlue,
                      marginRight: 12,
                    }}
                    name={spaceDropdownShown ? 'angle-up' : 'angle-down'}
                  />
                </View>
              </PressableStyled>
            </View>
          </View>
          <View style={{ marginStart: 20 }}>
            {spaceInvalid
              && <Text style={styles.errorText}>{'Please select a space'}</Text>}
            {spaceDropdownShown && <RegionDropdown
              regions={Object.values(selectedArea?.spaces ?? {})
                .sort((prev: Space, next: Space) => prev.Name.localeCompare(next.Name))}
              selectedRegion={selectedSpace}
              setSelectedRegion={setSelectedSpace}
              testID='edit-asset-space-list'
            />}
          </View>
        </View>}
    </View>
  </>;

const styles = StyleSheet.create({
  dropdownFieldLabel: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginTop: 16,
    marginBottom: 4,
  },
  dropdownButton: {
    ...Style.dropdown,
    paddingVertical: 9,
  },
  dropdownButtonInvalid: {
    borderColor: Colors.light.hereworksRed500,
  },
  connectorContainer: {
    flexDirection: 'row',
    marginBottom: 17,
    alignSelf: 'stretch',
  },
  floorAreaConnector: {
    width: 16,
    marginBottom: 2,
    alignSelf: 'stretch',
    borderStartWidth: 1,
    borderBottomWidth: 1,
    borderBottomStartRadius: 6,
    borderColor: Colors.light.hereworksBlue100,
  },
  connectorSpot: {
    width: 3,
    height: 6,
    borderTopStartRadius: 3,
    borderBottomStartRadius: 3,
    backgroundColor: Colors.light.hereworksBlue100,
    alignSelf: 'flex-end',
  },
  errorText: {
    color: Colors.light.hereworksRed500,
    fontFamily: 'Roboto_400Regular',
    marginTop: 4,
    fontSize: 12,
    lineHeight: 20,
    alignSelf: 'flex-start',
  },
  dropdownBoxText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    marginVertical: 9.5,
    marginLeft: 12,
    flex: 1,
  },
});

export default LocationInfo;
